import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 1: Multidimensional Human
 * Three nested spheres representing 3D/4D/5D consciousness
 */
export default function NestedSpheres({ audioData, isActive, position = [0, 0, 0] }) {
  const outerRef = useRef()
  const middleRef = useRef()
  const innerRef = useRef()

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  // Icosahedron geometry for outer sphere
  const icosahedronGeometry = useMemo(() => new THREE.IcosahedronGeometry(3, 1), [])

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    if (outerRef.current) {
      // Outer sphere: gold wireframe icosahedron, reacts to bass
      outerRef.current.rotation.y += delta * 0.08 + bass * 0.05
      outerRef.current.rotation.x = Math.sin(time * 0.3) * 0.2
      outerRef.current.scale.setScalar(1 + bass * 0.2)
    }

    if (middleRef.current) {
      // Middle sphere: cyan semi-transparent, reacts to mids
      middleRef.current.rotation.y -= delta * 0.1 + mids * 0.03
      middleRef.current.rotation.x = Math.cos(time * 0.4) * 0.15
      middleRef.current.scale.setScalar(1 + mids * 0.15)

      // Opacity pulse with mids
      middleRef.current.material.opacity = 0.3 + mids * 0.2
    }

    if (innerRef.current) {
      // Inner sphere: white glowing, reacts to highs
      innerRef.current.rotation.y += delta * 0.15 + highs * 0.07
      innerRef.current.rotation.z = Math.sin(time * 0.5) * 0.1
      innerRef.current.scale.setScalar(1 + highs * 0.3)

      // Emissive intensity pulse with highs
      innerRef.current.material.emissiveIntensity = 1 + highs * 2
    }
  })

  return (
    <group position={position}>
      {/* Outer sphere: Gold wireframe icosahedron */}
      <mesh ref={outerRef} geometry={icosahedronGeometry}>
        <meshBasicMaterial
          color="#FFD700"
          wireframe
          transparent
          opacity={0.6}
        />
      </mesh>

      {/* Middle sphere: Cyan semi-transparent */}
      <mesh ref={middleRef}>
        <sphereGeometry args={[2, 32, 32]} />
        <meshPhongMaterial
          color="#00FFFF"
          transparent
          opacity={0.3}
          shininess={100}
          emissive="#00FFFF"
          emissiveIntensity={0.2}
        />
      </mesh>

      {/* Inner sphere: White glowing */}
      <mesh ref={innerRef}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshStandardMaterial
          color="#FFFFFF"
          emissive="#FFFFFF"
          emissiveIntensity={1}
          transparent
          opacity={0.8}
        />
      </mesh>

      {/* Ambient light for the group */}
      <pointLight position={[0, 0, 0]} intensity={2} color="#FFFFFF" distance={10} />
    </group>
  )
}
